import { clearCookie, getCookie, setCookie } from "shared-utils/src/cookies";

export const SUPPORTED_LANGUAGES = ["ar", "en", "es", "th"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE = "en" as const;

export const COOKIE_DOMAIN = import.meta.env.VITE_LANGUAGE_COOKIE_DOMAIN;
export const COOKIE_EXPIRY_DAYS = 30; // matches bundle's COOKIE_EXPIRY_DAYS

export const isLocalizedPath = (path: string) =>
  SUPPORTED_LANGUAGES.some(
    (code) => path === `/${code}` || path.startsWith(`/${code}/`),
  );

export const isSupportedLanguage = (
  lang: string,
): lang is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
};

export const getLanguageFromPath = (path: string): SupportedLanguage => {
  const pathLang = path.split("/")[1];

  return SUPPORTED_LANGUAGES.includes(pathLang as SupportedLanguage)
    ? (pathLang as SupportedLanguage)
    : DEFAULT_LANGUAGE;
};

export const localizePath = (path: string, lang: string = DEFAULT_LANGUAGE) => {
  if (!path.startsWith("/") || isLocalizedPath(path)) {
    return path;
  }
  return `/${lang}${path}`;
};

export const setLanguageCookies = (language: SupportedLanguage) => {
  // if browser language is Thai, set thLang to true, ignore req_language
  if (language === "th") {
    setCookie("thLang", "true", COOKIE_DOMAIN, COOKIE_EXPIRY_DAYS);
  } else {
    // otherwise clear thLang cookie if there is one and set req_language cookie
    if (getCookie("thLang")) {
      clearCookie("thLang", COOKIE_DOMAIN);
    }
    setCookie("req_language", language, COOKIE_DOMAIN, COOKIE_EXPIRY_DAYS);
  }
};
export const resolveLanguage = () => {
  const browserLanguage = new Intl.Locale(navigator.language).language;
  const thLang = getCookie("thLang");
  const reqLanguage = getCookie("req_language");

  let appLanguage: SupportedLanguage = DEFAULT_LANGUAGE;

  // Override 1 - a thLang cookie value of True
  if (thLang && thLang === "true") {
    appLanguage = "th";
  }

  // Override 2 - a general language cookie
  else if (isSupportedLanguage(reqLanguage)) {
    appLanguage = reqLanguage;
  }

  // Override 3 - a supported browser language
  else if (isSupportedLanguage(browserLanguage)) {
    setLanguageCookies(browserLanguage);
    appLanguage = browserLanguage;
  }

  return appLanguage;
};

export const navigateLanguage = (language: SupportedLanguage) => {
  const pathParts = window.location.pathname.split("/");
  const urlLangPrefix = pathParts[1];

  if (urlLangPrefix == language) {
    return;
  }

  let newPath: string;
  if (urlLangPrefix.length === 2) {
    pathParts[1] = language;
    newPath = pathParts.join("/");
  } else {
    newPath = `/${language}${window.location.pathname}`;
  }

  window.history.replaceState(
    {},
    "",
    `${newPath}${window.location.search}${window.location.hash}`,
  );
};
